import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import Sidebar from './nav/Sidebar';
import BottomNav from './nav/BottomNav';
import Header from './nav/Header';
import MobileDrawer from './nav/MobileDrawer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Global responsive shell:
//  - Desktop (>= lg): left categorized sidebar + top header (quick links + avatar)
//  - Mobile  (<  lg): top header (hamburger + logo + bell) + bottom 5-tab nav + slide-in drawer
const Layout = ({ children }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [consultasTotal, setConsultasTotal] = useState(null);
  const location = useLocation();
  const { user } = useAuth();

  // Poll unread notifications (only when logged in). Silent on failure.
  const fetchUnread = useCallback(async () => {
    if (!user) { setUnreadCount(0); return; }
    try {
      const resp = await axios.get(`${API}/notifications/unread-count`, {
        timeout: 5000, withCredentials: true,
      });
      setUnreadCount(resp.data.unread || 0);
    } catch { /* silent */ }
  }, [user]);

  useEffect(() => {
    fetchUnread();
    const interval = setInterval(fetchUnread, 30000);
    return () => clearInterval(interval);
  }, [fetchUnread]);

  useEffect(() => { fetchUnread(); }, [location.pathname, fetchUnread]);

  // Public consultas counter (for drawer footer). Poll every 30s.
  useEffect(() => {
    let cancelled = false;
    const fetchTotal = async () => {
      try {
        const res = await fetch(`${API}/public/consultas/count`);
        if (!res.ok) return;
        const json = await res.json();
        if (!cancelled) setConsultasTotal(json?.total ?? null);
      } catch { /* silent */ }
    };
    fetchTotal();
    const id = setInterval(fetchTotal, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const badges = { unread: unreadCount };

  return (
    <div className="min-h-screen bg-[#F4F6F8]">
      <Sidebar badges={badges} />

      <div
        className="lg:ml-60 min-h-screen flex flex-col pb-[calc(64px+var(--safe-bottom,0px))] lg:pb-0"
        data-testid="main-wrapper"
      >
        <Header onOpenDrawer={() => setDrawerOpen(true)} unreadCount={unreadCount} />

        <main className="flex-1 p-3 sm:p-4 md:p-6 lg:p-8 custom-scrollbar" data-testid="main-content">
          <div className="max-w-[1440px] mx-auto">
            {user && user.tem_email_recuperacao === false && !sessionStorage.getItem('rec_email_dismiss') && (
              <div
                className="mb-4 flex items-center gap-3 p-3 rounded bg-[#FEF3C7] border border-[#FDE68A] text-[#92400E] text-xs"
                data-testid="user-recovery-banner"
              >
                <span className="flex-1">
                  <strong>Cadastre um e-mail de recuperação</strong> para garantir o acesso à sua conta caso perca a senha. Leva menos de 30 segundos.
                </span>
                <button
                  onClick={() => sessionStorage.setItem('rec_email_dismiss', '1') || window.location.assign('/perfil')}
                  className="px-3 py-1.5 rounded text-[11px] font-bold bg-[#92400E] text-white hover:bg-[#7C2D12]"
                  data-testid="user-recovery-banner-cta"
                >
                  Cadastrar
                </button>
                <button
                  onClick={() => { sessionStorage.setItem('rec_email_dismiss', '1'); window.location.reload(); }}
                  aria-label="Fechar"
                  className="text-[#92400E] hover:text-[#7C2D12]"
                >
                  ✕
                </button>
              </div>
            )}
            {children}
          </div>
        </main>
      </div>

      <BottomNav badges={badges} />
      <MobileDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        badges={badges}
        consultasTotal={consultasTotal}
      />
    </div>
  );
};

export default Layout;
